import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts';
import { LineChart, BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { Sec, ChartsRow, ChartCard, ChartHd, Plot } from '../styled';
import LoadingML from '@/components/LoadingML';
import { formatAxisWan } from '../../utils/format';

echarts.use([
  GridComponent,
  TooltipComponent,
  LegendComponent,
  CanvasRenderer,
  LineChart,
  BarChart,
]);

// 顶部水平图例（与原 ChartLegend HTML 视觉一致：10×10 圆角色块 + 间距 16 + 12px 灰字）
const TOP_LEGEND = {
  show: true,
  top: 8,
  left: 12,
  padding: 0,
  icon: 'roundRect',
  itemWidth: 10,
  itemHeight: 10,
  itemGap: 16,
  textStyle: { color: '#8c939e', fontSize: 12 },
};

// 左：详情页日访问趋势（PV / UV 双线）
// HTML：#1565c0 PV，#90caf9 UV；高度 204
const VisitChart = ({ data, loading }) => {
  const { labels = [], pv = [], uv = [] } = data || {};
  const option = useMemo(
    () => ({
      tooltip: { trigger: 'axis', confine: true },
      legend: { ...TOP_LEGEND, data: ['PV', 'UV'] },
      grid: { top: 36, left: 24, right: 8, bottom: 24, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: { color: '#8c939e', fontSize: 11 },
        axisLine: { lineStyle: { color: '#e2e7ec' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#8c939e', fontSize: 11 },
        splitLine: { lineStyle: { color: '#f0f1f5' } },
        axisTick: { show: false },
      },
      series: [
        {
          name: 'PV',
          type: 'line',
          smooth: false,
          showSymbol: false,
          lineStyle: { width: 2, color: '#1565c0' },
          itemStyle: { color: '#1565c0' },
          data: pv,
        },
        {
          name: 'UV',
          type: 'line',
          smooth: false,
          showSymbol: false,
          lineStyle: { width: 2, color: '#90caf9' },
          itemStyle: { color: '#90caf9' },
          data: uv,
        },
      ],
    }),
    [labels, pv, uv],
  );

  return (
    <ChartCard>
      <ChartHd>详情页日访问趋势</ChartHd>
      <Plot>
        <LoadingML loading={loading ? 1 : 0}>
          <ReactEChartsCore
            echarts={echarts}
            option={option}
            notMerge
            lazyUpdate
            style={{ width: '100%', height: '100%' }}
          />
        </LoadingML>
      </Plot>
    </ChartCard>
  );
};

// 右：日销售金额趋势（双折线：日实付 + 日早鸟）
// HTML：#1565c0 日实付，#90caf9 日早鸟；Y 轴以「万」为单位
const SalesTrendChart = ({ data, loading }) => {
  const { labels = [], paid = [], earlyBird = [] } = data || {};
  const option = useMemo(
    () => ({
      tooltip: { trigger: 'axis', confine: true },
      legend: { ...TOP_LEGEND, data: ['日实付金额', '日早鸟金额'] },
      grid: { top: 36, left: 24, right: 8, bottom: 24, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: { color: '#8c939e', fontSize: 11 },
        axisLine: { lineStyle: { color: '#e2e7ec' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: '#8c939e',
          fontSize: 11,
          formatter: formatAxisWan,
        },
        splitLine: { lineStyle: { color: '#f0f1f5' } },
        axisTick: { show: false },
      },
      series: [
        {
          name: '日实付金额',
          type: 'line',
          smooth: false,
          showSymbol: false,
          lineStyle: { width: 2, color: '#1565c0' },
          itemStyle: { color: '#1565c0' },
          data: paid,
        },
        {
          name: '日早鸟金额',
          type: 'line',
          smooth: false,
          showSymbol: false,
          lineStyle: { width: 2, color: '#90caf9' },
          itemStyle: { color: '#90caf9' },
          data: earlyBird,
        },
      ],
    }),
    [labels, paid, earlyBird],
  );

  return (
    <ChartCard>
      <ChartHd>日销售金额趋势</ChartHd>
      <Plot>
        <LoadingML loading={loading ? 1 : 0}>
          <ReactEChartsCore
            echarts={echarts}
            option={option}
            notMerge
            lazyUpdate
            style={{ width: '100%', height: '100%' }}
          />
        </LoadingML>
      </Plot>
    </ChartCard>
  );
};

const VisitSalesTrend = ({ trend, loading }) => (
  <Sec>
    <ChartsRow>
      <VisitChart data={trend} loading={loading} />
      <SalesTrendChart data={trend} loading={loading} />
    </ChartsRow>
  </Sec>
);

export default VisitSalesTrend;
